// Import necessary dependencies
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import Dropzone from 'react-dropzone'; // Dropzone for drag-and-drop file uploads
import { toast } from 'react-hot-toast'; // Toast notifications for feedback
import { uploadFileToFirebase } from '@/utils/FirebaseStorage'; // Utility function to upload image to Firebase Storage
import { doc, updateDoc } from 'firebase/firestore'; // Firebase Firestore utilities
import { db } from '@root/firebase';

// Import styles
import '@css/custom/dropzone/dropzone.css';
import 'https://unpkg.com/dropzone@5/dist/min/dropzone.min.js';

/**
 * ImageUploader Component
 * @param {string} uid - User ID used to store the image under the user's folder in Firestore.
 * @param {function} onUpload - Callback to notify the parent component when the image URL is ready.
 * @param {ref} ref - Exposes the `handleUpload` function for triggering uploads programmatically.
 */
const ImageUploader = forwardRef(({ uid, onUpload, onFileSelect }, ref) => {
  /**
   * STATE VARIABLES
   */
  const [selectedFile, setSelectedFile] = useState(null); // Stores the selected file object
  const [previewUrl, setPreviewUrl] = useState(''); // Stores the preview URL for the uploaded image
  const [loading, setLoading] = useState(false); // Tracks the loading state for the upload process

  /**
   * handleDrop
   * Purpose: Handles file drop events from Dropzone.
   * @param {Array} acceptedFiles - Array of files accepted by Dropzone
   */
  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0]; // Retrieve the first file from accepted files
    if (!file) {
      toast.error('No file selected'); // Notify the user if no file was dropped
      return;
    }
    const preview = URL.createObjectURL(file); // Generate a local preview URL for the image
    setSelectedFile(file); // Update state with the selected file
    setPreviewUrl(preview); // Update state with the preview URL

    if (typeof onFileSelect === 'function') {
      onFileSelect(file); // Notify the parent component
    }
  };

  /**
   * handleUpload
   * Purpose: Uploads the selected image to Firebase Storage and updates Firestore.
   * @param {string} folderName - The folder name in Firebase Storage where the image will be saved.
   * @param {string} fileName - The name of the file to store in Firebase Storage.
   * @param {string} fieldToUpdate - The Firestore document field to store the image URL.
   */
  const handleUpload = async (
    folderName,
    fileName,
    fieldToUpdate = null,
    root = `users/${uid}`,
    onProgress
  ) => {
    if (!selectedFile) {
      toast.error('No file to upload');
      return;
    }

    setLoading(true);
    try {
      const imageUrl = await uploadFileToFirebase(
        selectedFile,
        root,
        folderName,
        fileName,
        onProgress // Pass progress function to uploader
      );

      if (fieldToUpdate) {
        const userDocRef = doc(db, 'users', uid);
        await updateDoc(userDocRef, { [fieldToUpdate]: imageUrl });
      }

      if (typeof onUpload === 'function') {
        onUpload(imageUrl);
      }

      setSelectedFile(null);
      setPreviewUrl('');
      //toast.success('Image uploaded successfully!');
      return imageUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * handleRemoveFile
   * Purpose: Clears the selected file and its preview.
   */
  const handleRemoveFile = () => {
    setSelectedFile(null); // Reset selected file state
    setPreviewUrl(''); // Reset preview URL state
    //toast.info("File removed") // Notify the user that the file was removed
  };

  /**
   * Exposing handleUpload to parent via ref
   * Purpose: Allows the parent component to trigger `handleUpload` programmatically.
   */
  useImperativeHandle(ref, () => ({
    getSelectedFile: () => selectedFile,
    handleUpload, // already includes onProgress param now
  }));

  return (
    <div className='image-uploader'>
      {/* Dropzone Component */}
      <Dropzone
        onDrop={handleDrop} // Handles file drop events
        accept={{
          'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', '.svg'], // Acceptable file types
        }}
        multiple={false} // Allows only one file to be uploaded at a time
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps({ className: 'dropzone' })}>
            {/* Input for file selection */}
            <input {...getInputProps()} />
            <div className='dz-message needsclick'>
              {/* Display a preview if an image is uploaded */}
              {!previewUrl ? (
                <>
                  {/* Default Dropzone message */}
                  <div className='mb-3'>
                    <i className='display-4 text-muted mdi mdi-cloud-upload'></i>
                  </div>
                  <h5>Add a Picture</h5>
                </>
              ) : (
                // Image preview and remove button
                <div className='preview-container'>
                  <img
                    src={previewUrl} // Show the image preview
                    alt='Preview'
                    className='img-thumbnail'
                    style={{ width: '150px', height: '150px' }}
                  />
                  <button
                    className='btn btn-danger btn-sm mt-2'
                    onClick={handleRemoveFile} // Trigger file removal
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Dropzone>
    </div>
  );
});

// Set display name for debugging
ImageUploader.displayName = 'ImageUploader';

// Export the component for use in other parts of the application
export default ImageUploader;
